import type { TallyChartLedger } from "../data/tallyLedgerApi";
import type { TallyOpeningLedger } from "../data/tallyOpeningApi";

/**
 * Reserved Tally groups are not stored. The slug on each pulled row is the
 * group the classifier walked up to, and the label is that group's name.
 *
 * Order and labels match `OnboardingTallyAccountTypes::CREATABLE`.
 */
export const TALLY_RESERVED_GROUPS: { slug: string; label: string }[] = [
  { slug: "current_assets", label: "Current Assets" },
  { slug: "cash_in_hand", label: "Cash-in-Hand" },
  { slug: "stock_in_hand", label: "Stock-in-Hand" },
  { slug: "deposits_asset", label: "Deposits (Asset)" },
  { slug: "loans_advances_asset", label: "Loans & Advances (Asset)" },
  { slug: "fixed_assets", label: "Fixed Assets" },
  { slug: "investments", label: "Investments" },
  { slug: "misc_expenses_asset", label: "Misc. Expenses (ASSET)" },
  { slug: "branch_divisions", label: "Branch / Divisions" },
  { slug: "suspense_ac", label: "Suspense A/c" },
  { slug: "current_liabilities", label: "Current Liabilities" },
  { slug: "provisions", label: "Provisions" },
  { slug: "loans_liability", label: "Loans (Liability)" },
  { slug: "secured_loans", label: "Secured Loans" },
  { slug: "unsecured_loans", label: "Unsecured Loans" },
  { slug: "capital_account", label: "Capital Account" },
  { slug: "reserves_and_surplus", label: "Reserves & Surplus" },
  { slug: "direct_incomes", label: "Direct Incomes" },
  { slug: "indirect_incomes", label: "Indirect Incomes" },
  { slug: "sales_accounts", label: "Sales Accounts" },
  { slug: "direct_expenses", label: "Direct Expenses" },
  { slug: "indirect_expenses", label: "Indirect Expenses" },
  { slug: "purchase_accounts", label: "Purchase Accounts" },
];

const LABEL_BY_SLUG = new Map(TALLY_RESERVED_GROUPS.map((group) => [group.slug, group.label]));

export const TALLY_BANKS_SECTION = "banks";

export function tallyGroupLabel(slug: string | null): string {
  if (!slug) return "Other";
  return (
    LABEL_BY_SLUG.get(slug) ??
    slug
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

export interface TallyChartNode {
  ledger: TallyChartLedger;
  children: TallyChartNode[];
}

export interface TallyChartSection {
  slug: string;
  label: string;
  total: number;
  nodes: TallyChartNode[];
}

function rowId(ledger: TallyChartLedger): string {
  return ledger.account_id || ledger.guid || ledger.name;
}

/**
 * Nest pulled rows the way cockpit's chart does.
 *
 * A null parent_id means the parent is a reserved group that was never stored,
 * so the row is a root of its account_type section. A parent_id matches a
 * custom group that was stored.
 */
export function toTallyTree(rows: TallyChartLedger[]): TallyChartNode[] {
  const byId = new Map<string, TallyChartNode>();
  for (const ledger of rows) {
    byId.set(rowId(ledger), { ledger, children: [] });
  }

  const roots: TallyChartNode[] = [];
  for (const ledger of rows) {
    const node = byId.get(rowId(ledger));
    if (!node) continue;
    const parent = ledger.parent_id ? byId.get(ledger.parent_id) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }

  const byName = (left: TallyChartNode, right: TallyChartNode) =>
    left.ledger.name.localeCompare(right.ledger.name);
  const sort = (nodes: TallyChartNode[]) => {
    nodes.sort(byName);
    for (const node of nodes) sort(node.children);
  };
  sort(roots);

  return roots;
}

function filterTree(nodes: TallyChartNode[], query: string): TallyChartNode[] {
  const out: TallyChartNode[] = [];
  for (const node of nodes) {
    if (node.ledger.name.toLowerCase().includes(query)) {
      out.push(node);
      continue;
    }
    const children = filterTree(node.children, query);
    if (children.length > 0) out.push({ ledger: node.ledger, children });
  }
  return out;
}

export function buildTallyChartSections(
  ledgers: TallyChartLedger[],
  banks: TallyChartLedger[],
  query: string,
): TallyChartSection[] {
  const byType = new Map<string, TallyChartLedger[]>();
  for (const ledger of ledgers) {
    const key = ledger.account_type ?? "";
    const list = byType.get(key) ?? [];
    list.push(ledger);
    byType.set(key, list);
  }

  const known = new Set(TALLY_RESERVED_GROUPS.map((group) => group.slug));
  const slugs = [
    ...TALLY_RESERVED_GROUPS.map((group) => group.slug).filter((slug) => byType.has(slug)),
    ...Array.from(byType.keys()).filter((slug) => !known.has(slug)),
  ];

  const needle = query.trim().toLowerCase();
  const sections: TallyChartSection[] = [];
  for (const slug of slugs) {
    const rows = byType.get(slug) ?? [];
    const tree = toTallyTree(rows);
    const nodes = needle === "" ? tree : filterTree(tree, needle);
    if (needle !== "" && nodes.length === 0) continue;
    sections.push({
      slug: slug || "other",
      label: tallyGroupLabel(slug || null),
      total: rows.length,
      nodes,
    });
  }

  if (banks.length > 0) {
    const tree = toTallyTree(banks);
    const nodes = needle === "" ? tree : filterTree(tree, needle);
    if (needle === "" || nodes.length > 0) {
      sections.push({
        slug: TALLY_BANKS_SECTION,
        label: "Banks, credit cards, and overdrafts",
        total: banks.length,
        nodes,
      });
    }
  }

  return sections;
}

/**
 * Tally's reserved chart. A null parent is a primary group (the accordion).
 * The others are the reserved subgroups Tally nests under it: Cash-in-Hand
 * under Current Assets, Sundry Creditors under Current Liabilities.
 *
 * Party and bank groups are not in `TALLY_RESERVED_GROUPS` because the
 * classifier stops there and stores no account_type for them. They still
 * belong in this tree.
 */
const TALLY_OPENING_RESERVED: { key: string; label: string; parent: string | null; types?: string[] }[] = [
  { key: "current_assets", label: "Current Assets", parent: null },
  { key: "bank_accounts", label: "Bank Accounts", parent: "current_assets", types: ["bank"] },
  { key: "cash_in_hand", label: "Cash-in-Hand", parent: "current_assets" },
  { key: "deposits_asset", label: "Deposits (Asset)", parent: "current_assets" },
  { key: "loans_advances_asset", label: "Loans & Advances (Asset)", parent: "current_assets" },
  { key: "stock_in_hand", label: "Stock-in-Hand", parent: "current_assets" },
  { key: "sundry_debtors", label: "Sundry Debtors", parent: "current_assets" },
  { key: "fixed_assets", label: "Fixed Assets", parent: null },
  { key: "investments", label: "Investments", parent: null },
  { key: "misc_expenses_asset", label: "Misc. Expenses (ASSET)", parent: null },
  { key: "branch_divisions", label: "Branch / Divisions", parent: null },
  { key: "suspense_ac", label: "Suspense A/c", parent: null },
  { key: "current_liabilities", label: "Current Liabilities", parent: null },
  { key: "duties_taxes", label: "Duties & Taxes", parent: "current_liabilities" },
  { key: "provisions", label: "Provisions", parent: "current_liabilities" },
  { key: "sundry_creditors", label: "Sundry Creditors", parent: "current_liabilities" },
  { key: "credit_cards", label: "Credit Cards", parent: "current_liabilities", types: ["credit", "credit_card"] },
  { key: "loans_liability", label: "Loans (Liability)", parent: null },
  { key: "bank_od", label: "Bank OD A/c", parent: "loans_liability", types: ["overdraft"] },
  { key: "secured_loans", label: "Secured Loans", parent: "loans_liability" },
  { key: "unsecured_loans", label: "Unsecured Loans", parent: "loans_liability" },
  { key: "capital_account", label: "Capital Account", parent: null },
  { key: "reserves_and_surplus", label: "Reserves & Surplus", parent: "capital_account" },
  { key: "direct_incomes", label: "Direct Incomes", parent: null },
  { key: "indirect_incomes", label: "Indirect Incomes", parent: null },
  { key: "sales_accounts", label: "Sales Accounts", parent: null },
  { key: "direct_expenses", label: "Direct Expenses", parent: null },
  { key: "indirect_expenses", label: "Indirect Expenses", parent: null },
  { key: "purchase_accounts", label: "Purchase Accounts", parent: null },
];

const OPENING_RESERVED_BY_KEY = new Map(TALLY_OPENING_RESERVED.map((group) => [group.key, group]));
const OPENING_SECTION_BY_PARENT = new Map<string, string>();
const OPENING_SECTION_BY_TYPE = new Map<string, string>();
for (const group of TALLY_OPENING_RESERVED) {
  OPENING_SECTION_BY_PARENT.set(group.label.toLowerCase(), group.key);
  OPENING_SECTION_BY_TYPE.set(group.key, group.key);
  for (const type of group.types ?? []) OPENING_SECTION_BY_TYPE.set(type, group.key);
}

export interface TallyOpeningNode {
  row: TallyOpeningLedger;
  children: TallyOpeningNode[];
}

export interface TallyOpeningSection {
  key: string;
  label: string;
  leaves: number;
  nodes: TallyOpeningNode[];
}

function openingNodeId(row: TallyOpeningLedger): string {
  return row.account_id || row.guid;
}

/** The reserved group a root hangs from. Nested rows inherit it through the tree. */
function openingSectionKey(row: TallyOpeningLedger): string {
  const parent = (row.parent_name ?? "").trim().toLowerCase();
  if (parent !== "" && OPENING_SECTION_BY_PARENT.has(parent)) {
    return OPENING_SECTION_BY_PARENT.get(parent) ?? "other";
  }
  if (row.account_type && OPENING_SECTION_BY_TYPE.has(row.account_type)) {
    return OPENING_SECTION_BY_TYPE.get(row.account_type) ?? "other";
  }
  return "other";
}

function countLeaves(nodes: TallyOpeningNode[]): number {
  let count = 0;
  for (const node of nodes) {
    count += node.row.is_group ? countLeaves(node.children) : 1;
  }
  return count;
}

function reservedOpeningRow(key: string, label: string): TallyOpeningLedger {
  return {
    guid: `reserved:${key}`,
    name: label,
    kind: "group",
    account_id: `reserved:${key}`,
    parent_id: null,
    parent_name: null,
    account_type: key,
    is_group: true,
    tally_amount: 0,
    tally_side: null,
    amount: 0,
    side: null,
    saved: false,
    status: "group",
    error: null,
  };
}

const byOpeningName = (left: TallyOpeningNode, right: TallyOpeningNode) => {
  if (left.row.is_group !== right.row.is_group) return left.row.is_group ? -1 : 1;
  return left.row.name.localeCompare(right.row.name);
};

/**
 * Primary reserved group, then the reserved subgroup or customer-created
 * group under it, then ledgers.
 *
 * A group with no ledgers under it is dropped: an accordion that opens onto
 * nothing is not a place to type a figure.
 */
export function buildTallyOpeningSections(rows: TallyOpeningLedger[]): TallyOpeningSection[] {
  const referenced = new Set(rows.map((row) => row.parent_id).filter((id): id is string => Boolean(id)));
  const byId = new Map<string, TallyOpeningNode>();
  for (const row of rows) {
    if (row.is_group && !referenced.has(openingNodeId(row))) continue;
    byId.set(openingNodeId(row), { row, children: [] });
  }

  const roots: TallyOpeningNode[] = [];
  for (const row of rows) {
    const node = byId.get(openingNodeId(row));
    if (!node) continue;
    const parent = row.parent_id ? byId.get(row.parent_id) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }

  const sort = (nodes: TallyOpeningNode[]) => {
    nodes.sort(byOpeningName);
    for (const node of nodes) sort(node.children);
  };
  sort(roots);

  const buckets = new Map<string, TallyOpeningNode[]>();
  for (const root of roots) {
    if (root.row.is_group && countLeaves(root.children) === 0) continue;
    const key = openingSectionKey(root.row);
    const list = buckets.get(key) ?? [];
    list.push(root);
    buckets.set(key, list);
  }

  const reservedChildren = new Map<string, string[]>();
  for (const group of TALLY_OPENING_RESERVED) {
    if (group.parent === null) continue;
    const list = reservedChildren.get(group.parent) ?? [];
    list.push(group.key);
    reservedChildren.set(group.parent, list);
  }

  function reservedNode(key: string): TallyOpeningNode | null {
    const group = OPENING_RESERVED_BY_KEY.get(key);
    if (!group) return null;
    const children: TallyOpeningNode[] = [];
    for (const childKey of reservedChildren.get(key) ?? []) {
      const child = reservedNode(childKey);
      if (child) children.push(child);
    }
    children.push(...(buckets.get(key) ?? []));
    children.sort(byOpeningName);
    if (countLeaves(children) === 0) return null;
    return { row: reservedOpeningRow(key, group.label), children };
  }

  const sections: TallyOpeningSection[] = [];
  for (const group of TALLY_OPENING_RESERVED) {
    if (group.parent !== null) continue;
    const node = reservedNode(group.key);
    if (!node) continue;
    sections.push({
      key: group.key,
      label: group.label,
      leaves: countLeaves(node.children),
      nodes: node.children,
    });
  }

  const other = (buckets.get("other") ?? []).filter((node) => !node.row.is_group || countLeaves(node.children) > 0);
  const otherLeaves = countLeaves(other);
  if (otherLeaves > 0) {
    sections.push({ key: "other", label: "Other", leaves: otherLeaves, nodes: other });
  }

  return sections;
}
